import { useRequest } from 'ahooks';
import { message } from 'antd';
import { useCallback, useContext, useEffect } from 'react';
import { useTranslate } from '@tolgee/react';
import {
  statusCountMap,
  todoListMap,
  todoCurrentId,
  listLoading,
  todoStatus,
  relatedMsgIdInfo,
  locateTodoId,
  TodoListMap,
} from './state';
import {
  getTodoList,
  getTodoDetail,
  getAssistants,
  updateTodo,
  closeTodo,
  reopenTodo,
  deleteTodo,
} from './api';
import { ToDoBundleContext } from './context';
import {
  list2Map,
  getStatusMap,
  deriveTodoStatusFromRelated,
  deriveCurrentIdFromList,
  extractRelatedMsgIdInfo,
} from './todoBusinessUtils';
import { noop } from 'lodash-es';
import { useUpdateSiderTodoBadge } from 'hooks/business/useSiderTodoBadge';
import { useSetAtom, useAtomValue, useAtom } from 'jotai';
import { useAtomCallback, useResetAtom } from 'jotai/utils';

let refreshFn: () => Promise<any> = () => Promise.resolve();
let detailRefreshFn: (id: string) => void = noop;

const useUpdateRelatedMsgIdInfo = () => {
  const setRelatedMsgIdInfo = useSetAtom(relatedMsgIdInfo);
  const locateId = useAtomValue(locateTodoId);
  const [currentStatus, setTodoStatus] = useAtom(todoStatus);

  return (statusTodoMap: TodoListMap) => {
    const relatedList = extractRelatedMsgIdInfo(statusTodoMap);
    setRelatedMsgIdInfo(relatedList);
    const statusMap = getStatusMap(statusTodoMap);
    const nextStatus = deriveTodoStatusFromRelated(relatedList, locateId, statusMap, currentStatus);
    setTodoStatus(nextStatus);
  };
};

export const useList = () => {
  const setStatusMap = useSetAtom(statusCountMap);
  const setList = useSetAtom(todoListMap);
  const resetList = useResetAtom(todoListMap);
  const resetLocateTodoId = useResetAtom(locateTodoId);
  const setLoading = useSetAtom(listLoading);
  const setTodoStatus = useSetAtom(todoStatus);
  const setTodoCurrentId = useSetAtom(todoCurrentId);
  const { storeId, buyerId, externalBuyerId } = useContext(ToDoBundleContext);

  const locateId = useAtomValue(locateTodoId);
  const currentStatus = useAtomValue(todoStatus);
  const currentTodoListMap = useAtomValue(todoListMap);

  useEffect(() => {
    setTodoStatus('1');
    resetList();
    resetLocateTodoId();
  }, [buyerId, setTodoStatus, resetList, resetLocateTodoId]);

  useEffect(() => {
    const currentList = currentTodoListMap[currentStatus] || [];
    const nextId = deriveCurrentIdFromList(currentList, locateId);
    setTodoCurrentId(nextId ?? '');
  }, [currentTodoListMap, currentStatus, locateId, setTodoCurrentId]);

  const updateRelatedMsgIdInfo = useUpdateRelatedMsgIdInfo();
  const updateSiderTodoBadge = useUpdateSiderTodoBadge();

  const { loading, runAsync } = useRequest(
    () => getTodoList({ storeId, buyerId, tradeBuyerId: externalBuyerId }),
    {
      manual: false,
      refreshDeps: [buyerId],
      onSuccess: (res) => {
        const todoInstances = res?.todoInstanceDetails || [];
        const todoListMap = list2Map(todoInstances);
        setList(todoListMap);
        updateRelatedMsgIdInfo(todoListMap);
        updateSiderTodoBadge(todoListMap);
        setStatusMap(getStatusMap(todoListMap));
      },
      ready: !!storeId && !!buyerId,
      debounceWait: 1000,
    },
  );

  refreshFn = runAsync;

  useEffect(() => {
    setLoading(loading);
  }, [setLoading, loading]);
};

export const useRefresh = () => {
  return useCallback((fn?: () => void) => {
    setTimeout(() => {
      refreshFn?.()?.then(() => {
        fn?.();
      });
    });
  }, []);
};

export const useDetail = () => {
  const currentId = useAtomValue(todoCurrentId);
  const updateSiderTodoBadge = useUpdateSiderTodoBadge();

  const { data, loading, runAsync } = useRequest<Todo.TodoInstance, any[]>(
    (id: string) => getTodoDetail(id),
    {
      manual: true,
    },
  );

  /**
   * 更新待办状态，同步到待办提示和小红点
   */
  const updateTodoBadge = useAtomCallback(
    useCallback(
      (get, _, todoInstance: Todo.TodoInstance) => {
        const listMap = get(todoListMap);
        const list = Object.values(listMap).flat();
        // 如果 id 存在，则更新对应的 todoInstance，否则新增
        const existIndex = list.findIndex((item) => item.id === todoInstance.id);
        let updatedStatusList: Todo.TodoInstance[] = [];
        if (existIndex !== -1) {
          // 更新已存在的 todoInstance
          updatedStatusList = [
            ...list.slice(0, existIndex),
            todoInstance,
            ...list.slice(existIndex + 1),
          ];
        } else {
          // 不存在则新增
          updatedStatusList = [...list, todoInstance];
        }
        const newTodoListMap = list2Map(updatedStatusList);
        updateSiderTodoBadge(newTodoListMap);
      },
      [updateSiderTodoBadge],
    ),
  );

  useEffect(() => {
    if (currentId) {
      runAsync(currentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  detailRefreshFn = (id: string) => {
    runAsync(id).then((res) => {
      updateTodoBadge(res);
    });
  };

  return {
    detail: data,
    loading,
  };
};

export const useAssistants = (internalStoreId: string) => {
  const { data, loading } = useRequest(() => getAssistants(internalStoreId), {
    refreshDeps: [internalStoreId],
    ready: !!internalStoreId,
  });
  return {
    data: data?.data?.assistants || [],
    loading,
  };
};

export const useUpdateTodo = () => {
  const { runAsync } = useRequest((data) => updateTodo(data), {
    manual: true,
  });

  return useAtomCallback(
    useCallback(
      (get, _, touchedValue: Record<string, any>) => {
        const currentId = get(todoCurrentId);
        return runAsync({ id: currentId, ...touchedValue }).then(() => {
          setTimeout(() => {
            detailRefreshFn?.(currentId);
          });
        });
      },
      [runAsync],
    ),
  );
};

export const useCloseTodo = () => {
  const { t } = useTranslate();
  const { runAsync } = useRequest((data) => closeTodo(data), {
    manual: true,
  });

  return useAtomCallback(
    useCallback(
      (get, _, sendReplyMsg: boolean = false, _id?: string) => {
        const id = _id || get(todoCurrentId);
        return runAsync({ id, sendReplyMsg })
          .then(() => {
            message.success(
              sendReplyMsg ? t('reply.已发送') : t('scene.chatWorkspace.taskTab.feedback.closed'),
            );
            setTimeout(() => {
              detailRefreshFn?.(id);
            });
          })
          .catch(() => {
            message.error(t('common.feedback.saveFailed'));
          });
      },
      [runAsync, t],
    ),
  );
};

export const useReopenTodo = () => {
  const currentId = useAtomValue(todoCurrentId);
  const { t } = useTranslate();
  const { runAsync } = useRequest((data) => reopenTodo(data), {
    manual: true,
  });

  return useCallback(() => {
    return runAsync({ id: currentId })
      .then(() => {
        message.success(t('scene.chatWorkspace.taskTab.feedback.operationSuccess'));
        setTimeout(() => {
          detailRefreshFn?.(currentId);
        });
      })
      .catch(() => {
        message.error(t('common.feedback.saveFailed'));
      });
  }, [runAsync, currentId, t]);
};

/**
 * 删除待办，成功后刷新待办列表（删除逻辑与轻量布局保持一致）
 */
export const useDeleteTodo = () => {
  const { t } = useTranslate();
  const refresh = useRefresh();
  const { runAsync, loading } = useRequest((id: string) => deleteTodo(id), {
    manual: true,
  });

  const remove = useCallback(
    (id: string) => {
      return runAsync(id)
        .then(() => {
          message.success(t('operation.deleteSucceed'));
          // 刷新列表后，当前待办若已被删除会自动切换到其他待办
          refresh();
          return true;
        })
        .catch(() => {
          message.error(t('operation.deleteFailed'));
          return false;
        });
    },
    [refresh, runAsync, t],
  );

  return { deleteTodo: remove, loading };
};
